import React, { useState} from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Heading, FormControl, FormLabel, Input as ChakraInput, Button as ChakraButton,
  Flex, useToast, VStack, Select, InputGroup, InputRightElement, Text
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import QRCodeGenerator from '../components/QRCodeGenerator';
import axios from 'axios';

const SignUpPage = () => {
  const [form, setForm] = useState({
    name: '',
    employeeNumber: '',
    role: '',
    department: '',
    userIdOrEmail: '',
    password: '',
  });
  const [customRoleInput, setCustomRoleInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [qrValue, setQRValue] = useState('');
  const [passwordValidations, setPasswordValidations] = useState({
    length: false, uppercase: false, lowercase: false, number: false, specialChar: false,
  });

  const toast = useToast();


  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === 'password') {
      if (!passwordTouched && value.length > 0) setPasswordTouched(true);
      setPasswordValidations({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /[0-9]/.test(value),
        specialChar: /[^A-Za-z0-9]/.test(value),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEmail = form.userIdOrEmail.includes('@');
    const userData = {
      name: form.name,
      empno: form.employeeNumber,
      role: form.role === 'Others' ? customRoleInput : form.role,
      department: form.department,
      userid: isEmail ? '' : form.userIdOrEmail,
      email: isEmail ? form.userIdOrEmail : '',
      password: form.password,
    };

    if (!userData.name.trim() || !userData.password.trim() || !form.userIdOrEmail.trim()) {
      toast({ title: 'Required fields missing.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (!Object.values(passwordValidations).every(Boolean)) {
      toast({ title: 'Password does not meet requirements.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    try {
      const response = await axios.post('https://ssi-auth-backend.staging.trustgrid.com/register', userData);
      const result = response.data;
      // Only userID in URL, no metadata
      const qrUrl = JSON.stringify(result);
      setQRValue(qrUrl);
      setSubmitted(true);
      toast({ title: 'Registered successfully!', status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      console.error('Registration failed:', error);
      toast({
        title: 'Registration failed',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex justify="center" align="center" minH="100vh" bg="gray.50" p={4}>
      <Box bg="white" p={8} rounded="lg" shadow="lg" w="full" maxW="md">
        {!submitted ? (
          <form onSubmit={handleSubmit}>
            <Heading mb={6} textAlign="center" size="lg" color="blue.600">
              Sign up — credentials go right to your wallet!
            </Heading>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Full Name</FormLabel>
                <ChakraInput name="name" placeholder="John Doe" value={form.name} onChange={handleChange} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Employee Number</FormLabel>
                <ChakraInput name="employeeNumber" placeholder="EMP12345" value={form.employeeNumber} onChange={handleChange} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Role</FormLabel>
                <Select name="role" placeholder="Select role" value={form.role} onChange={handleChange}>
                  <option value="Manager">Manager</option>
                  <option value="Developer">Developer</option>
                  <option value="Designer">Designer</option>
                  <option value="Admin">Admin</option>
                  <option value="Others">Others</option>
                </Select>
              </FormControl>
              {form.role === 'Others' && (
                <FormControl mt={2}>
                  <FormLabel>Specify your role</FormLabel>
                  <ChakraInput
                    name="customRoleInput"
                    placeholder="Enter your role"
                    value={customRoleInput}
                    onChange={(e) => setCustomRoleInput(e.target.value)}
                  />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Department</FormLabel>
                <Select name="department" placeholder="Select department" value={form.department} onChange={handleChange}>
                  <option value="IT">IT</option>
                  <option value="HR">HR</option>
                  <option value="Finance">Finance</option>
                  <option value="Operations">Operations</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>User ID / Email</FormLabel>
                <ChakraInput name="userIdOrEmail" placeholder="Email or User ID" value={form.userIdOrEmail} onChange={handleChange} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <ChakraInput
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={handleChange}
                  />
                  <InputRightElement h="full">
                    <ChakraButton variant="ghost" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    </ChakraButton>
                  </InputRightElement>
                </InputGroup>
                {passwordTouched && (
                  <Box fontSize="sm" mt={2}>
                    <Text color={passwordValidations.length ? 'green.500' : 'red.500'}>• At least 8 characters</Text>
                    <Text color={passwordValidations.uppercase ? 'green.500' : 'red.500'}>• At least one uppercase letter</Text>
                    <Text color={passwordValidations.lowercase ? 'green.500' : 'red.500'}>• At least one lowercase letter</Text>
                    <Text color={passwordValidations.number ? 'green.500' : 'red.500'}>• At least one number</Text>
                    <Text color={passwordValidations.specialChar ? 'green.500' : 'red.500'}>• At least one special character</Text>
                  </Box>
                )}
              </FormControl>
              <ChakraButton colorScheme="blue" type="submit" w="full" mt={4}>
                Register
              </ChakraButton>
            </VStack>
          </form>
        ) : (
          <>
            <Heading mb={4} textAlign="center" size="md" color="blue.600">
              Scan This QR to Login
            </Heading>
            <Flex justify="center">
              <Box display="inline-block" border="1px solid #E2E8F0" rounded="md" p={2}>
                <QRCodeGenerator value={qrValue} />
              </Box>
            </Flex>
            <Link to="/login" style={{ width: '100%' }}>
              <ChakraButton colorScheme="blue" mt={4} w="full">
                Proceed to Login
              </ChakraButton>
            </Link>
          </>
        )}
      </Box>
    </Flex>
  );
};

export default SignUpPage;
